import * as Sentry from '@sentry/react'
import { api, makeApiRequest } from './_init'
import { unwrapEnvelope } from 'domain/shared/envelope'
import { formatDateYYYYMMDD } from 'utils/helpers'
import { LOADING, LOADING_PAGE, UPDATE, OVERWRITE, FAIL } from '../reducers/default'
import { rankCampaignList } from './models'
import campaignDiscoveryApi from 'domain/campaignDiscovery/api'

export async function fetchCampaigns(
  dispatch,
  event_id,
  settings = {},
  onlyActive = false,
  controller = null,
) {
  if (!event_id) {
    console.log('event id missing in fetch for fetchCampaigns')
    return
  }

  const { page = 1, page_limit = 50 } = settings
  const PAGE_LIMIT = page_limit > 0 ? page_limit : 50
  dispatch({ type: page > 1 ? LOADING_PAGE : LOADING })

  const { data, error, status } = await campaignDiscoveryApi.fetchAsync(event_id, {
    page,
    pageLimit: PAGE_LIMIT,
    signal: controller?.signal,
  })

  if (status !== 200) {
    // An aborted fetch (e.g. a superseded page/filter change, or the
    // component unmounting) isn't a real failure — unwrapEnvelope doesn't
    // preserve makeApiRequest's `cancelled` flag, so check the controller
    // directly instead of reporting every cancellation as an error.
    if (!controller?.signal?.aborted) {
      console.error('Error fetching campaigns: ', error)
      Sentry.captureException(error)
      dispatch({ type: FAIL, payload: 'Error fetching data.' })
    }
    return
  }

  const response = {
    total: data.total,
    maxPage: Math.ceil(data.total / PAGE_LIMIT),
    page: page,
    items: rankCampaignList(data.items),
  }

  // filters only running campaigns from unused ones
  if (onlyActive) {
    response.items = response.items.filter((campaign) => campaign.status === 'running')
  }

  dispatch({ type: page > 1 ? UPDATE : OVERWRITE, payload: response })
}

export async function fetchCampaignCtasAsync(
  dispatch,
  goal,
  hasPixel,
  leadFormId,
  usesPlacementMedia = false,
  controller = null,
) {
  dispatch({ type: LOADING })
  const goalParts = (goal ?? '').split('.')
  const params = new URLSearchParams()
  params.append('goal', goalParts[goalParts.length - 1])
  if (hasPixel != null) params.append('has_pixel', hasPixel)
  if (leadFormId != null) params.append('lead_form_id', leadFormId)
  if (usesPlacementMedia) params.append('uses_placement_media', true)

  const endpoint = `/integrations/connect/facebook/assets/call_to_actions?${params.toString()}`
  const response = await makeApiRequest(api, 'get', endpoint, undefined, {
    signal: controller?.signal,
  })
  const { data, error } = unwrapEnvelope(response)

  if (error) {
    // A superseded/unmounted request must not clear the option list — FAIL
    // empties items, and the request that replaces this one may never arrive.
    if (!controller?.signal?.aborted) {
      dispatch({ type: FAIL, payload: 'common.errors.inServerResponse' })
    }
    return
  }

  dispatch({
    type: OVERWRITE,
    payload: {
      total: data?.length ?? 0,
      page: 0,
      maxPage: data?.length ?? 0,
      items: data,
    },
  })
}

export const getUrlMetaTags = async (url) => {
  try {
    const result = await api.get(`/campaigns/meta_tags?url=${url}`)
    if (result.status !== 200) {
      return { error: true, success: false, payload: 'common.errors.inServerResponse' }
    }

    const parsedPayload = await JSON.parse(result.data.tags)
    if (parsedPayload.error) {
      return {
        error: true,
        success: false,
        payload: 'Website blocks crawlers or is down',
        message: parsedPayload,
      }
    }

    return {
      error: false,
      success: true,
      tags: JSON.parse(result.data.tags),
    }
  } catch (error) {
    console.log('error in catch block ', error)
    return { error: true, success: false, payload: 'common.errors.inServerResponse' }
  }
}

export const fetchCustomEventTypes = async (
  dispatch,
  goal,
  hasPixel = true,
  leadFormId = null,
  controller = null,
) => {
  dispatch({ type: LOADING })
  try {
    const result = await api.get('/integrations/connect/facebook/assets/event_types', {
      params: {
        goal,
        has_pixel: hasPixel,
        lead_form_id: leadFormId,
      },
      signal: controller?.signal,
    })
    dispatch({ type: OVERWRITE, payload: result.data })
  } catch (error) {
    dispatch({ type: FAIL, payload: 'common.errors.inServerResponse' })
  }
}

const campaignsApi = {
  fetchCurrentCampaignRuntimeAndBudget: async (eventId) => {
    return await makeApiRequest(api, 'get', `/campaigns/current_runtime_budget/${eventId}`)
  },
  getDailyBudgetPreview: async (eventId, budget, startDate, endDate) => {
    startDate = startDate ? formatDateYYYYMMDD(startDate) : undefined
    endDate = formatDateYYYYMMDD(endDate)
    let url = `/campaigns/new_runtime_budget?eid=${eventId}&budget=${budget}&end_date=${endDate}`
    if (startDate) {
      url += `&start_date=${startDate}`
    }
    return await makeApiRequest(api, 'get', url)
  },
  postBudgetAndRuntime: async (eventId, budget, startDate, endDate) => {
    startDate = startDate ? formatDateYYYYMMDD(startDate) : undefined
    endDate = formatDateYYYYMMDD(endDate)
    let url = `/campaigns/new_runtime_budget?eid=${eventId}&budget=${budget}&end_date=${endDate}`
    if (startDate) {
      url += `&start_date=${startDate}`
    }
    return await makeApiRequest(api, 'post', url)
  },
}

export default campaignsApi
